import React, { useState } from "react";
import Typography from "style-guide/component/Typography";
import Button from "style-guide/component/Button";
import Input from "style-guide/component/Input";
import TextArea from "style-guide/component/Textarea";
import { ContactForm as ContactFormType } from "./types";
import { PRIORITY_OPTIONS } from "./constants";
import { QuestionCircleFilled } from "@ant-design/icons";
import { AppDispatch, RootState } from "redux/store";
import { useSelector, useDispatch } from "react-redux";
import { contactSupport } from "../../../../redux/slice/rfpModuleSlice/application";

interface ContactFormProps {
  className?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ className = "" }) => {
  const { userProfile } = useSelector(
    (state: RootState) => state.applicationData
  );
  const { isLoading } = useSelector(
    (state: RootState) => state.applicationData
  );
  const dispatch = useDispatch<AppDispatch>();

  const [contactForm, setContactForm] = useState<ContactFormType>({
    name:
      `${userProfile?.userDetails?.[0]?.firstName} ${userProfile?.userDetails?.[0]?.lastName}` ||
      "",
    email: userProfile?.userDetails?.[0]?.email || "",
    subject: "",
    message: "",
    priority: "medium",
  });
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    subject?: string;
    message?: string;
  }>({});

  const handleInputChange = (field: keyof ContactFormType, value: string) => {
    setContactForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {};

    // Required field validation
    if (!contactForm.name.trim()) {
      errors.name = "Full name is required";
    }

    if (!contactForm.email.trim()) {
      errors.email = "Email address is required";
    } else {
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactForm.email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    if (!contactForm.subject.trim()) {
      errors.subject = "Subject is required";
    }

    if (!contactForm.message.trim()) {
      errors.message = "Message is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearForm = () => {
    setContactForm({
      name:
        `${userProfile?.userDetails?.[0]?.firstName} ${userProfile?.userDetails?.[0]?.lastName}` ||
        "",
      email: userProfile?.userDetails?.[0]?.email || "",
      subject: "",
      message: "",
      priority: "medium",
    });
    setFieldErrors({});
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) return;

    const result = await dispatch(contactSupport(contactForm));
    if (result === 200) {
      clearForm();
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border p-8 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <QuestionCircleFilled
          className="text-blue-600"
          style={{ fontSize: "46px" }}
        />
        <div>
          <Typography variant="Heading 2" text="Contact Support" />
          <Typography
            variant="Base"
            text="Can't find what you're looking for? We're here to help!"
            className="text-gray-600"
          />
        </div>
      </div>

      {/* Response Time Info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Typography
            variant="Base"
            text="Expected Response Time"
            className="font-medium text-green-800"
          />
        </div>
        <Typography
          variant="Base"
          text="We typically respond within 24 hours during business days. For urgent issues, please mark your priority as 'High'."
          className="text-green-700"
        />
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Typography
              variant="SM"
              text="Full Name *"
              className="mb-2 font-medium text-gray-700"
            />
            <Input
              type="text"
              value={contactForm.name}
              disabled
              className={`transition-all duration-200 ${fieldErrors.name ? "border-red-500" : ""}`}
            />
            {fieldErrors.name && (
              <Typography
                variant="SM"
                text={fieldErrors.name}
                className="text-red-500 mt-1"
              />
            )}
          </div>
          <div>
            <Typography
              variant="SM"
              text="Email Address *"
              className="mb-2 font-medium text-gray-700"
            />
            <Input
              type="email"
              value={contactForm.email}
              disabled
              className={`transition-all duration-200 ${fieldErrors.email ? "border-red-500" : ""}`}
            />
            {fieldErrors.email && (
              <Typography
                variant="SM"
                text={fieldErrors.email}
                className="text-red-500 mt-1"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Typography
              variant="SM"
              text="Subject *"
              className="mb-2 font-medium text-gray-700"
            />
            <Input
              type="text"
              placeholder="Brief description of your inquiry"
              value={contactForm.subject}
              disabled={isLoading}
              onChange={(e) => handleInputChange("subject", e.target.value)}
              className={`transition-all duration-200 ${fieldErrors.subject ? "border-red-500" : ""}`}
            />
            {fieldErrors.subject && (
              <Typography
                variant="SM"
                text={fieldErrors.subject}
                className="text-red-500 mt-1"
              />
            )}
          </div>
          <div>
            <Typography
              variant="SM"
              text="Priority Level"
              className="mb-2 font-medium text-gray-700"
            />
            <select
              value={contactForm.priority}
              onChange={(e) =>
                handleInputChange(
                  "priority",
                  e.target.value as "low" | "medium" | "high"
                )
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-sm focus:border-blue-500 focus:outline-none transition-all duration-200"
            >
              {PRIORITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Typography
            variant="SM"
            text="Message *"
            className="mb-2 font-medium text-gray-700"
          />
          <TextArea
            placeholder="Please provide detailed information about your question or issue. Include any error messages, steps you've tried, or specific features you need help with..."
            value={contactForm.message}
            disabled={isLoading}
            onChange={(e) => handleInputChange("message", e.target.value)}
            className={`min-h-36 transition-all duration-200 ${fieldErrors.message ? "border-red-500" : ""}`}
          />
          {fieldErrors.message && (
            <Typography
              variant="SM"
              text={fieldErrors.message}
              className="text-red-500 mt-1"
            />
          )}
          <div className="text-right mt-1">
            <Typography
              variant="SM"
              text={`${contactForm.message.length}/1000 characters`}
              className="text-gray-400 text-xs"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>🔒</span>
            <Typography
              variant="Base"
              text="Your information is secure and will only be used to respond to your inquiry."
            />
          </div>
          <div className="flex gap-3">
            <Button
              type={isLoading ? "Disabled" : "Primary"}
              buttonText={isLoading ? "Sending..." : "Send Message"}
              onClick={() => handleSubmit()}
              loading={isLoading}
              size="Medium"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
